/**
 * Hook TanStack Query tipizzati per le entità AppSync.
 *
 * Pattern:
 *   - Query key gerarchico per granularità di invalidazione
 *     (es. ['invoices', codice_cliente, codice_contratto])
 *   - `enabled` solo quando le chiavi di input sono valide (no fetch con stringa vuota)
 *   - staleTime ragionevole (1-5 min) per evitare refetch eccessivi
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  fetchBolletteContratto,
  fetchCliente,
  fetchConsumoAnno,
  fetchConsumoBimestre,
  fetchCurrDay,
  fetchCurrHour,
  fetchCurrMonth,
  fetchCurrPower,
  fetchDettaglioBolletta,
  fetchLetture,
  fetchMessaggi,
  fetchNews,
  fetchPrevMonth,
  fetchPromozioni,
  markBollettaVista,
  markMessaggioRead,
  sendAutoLettura,
  updateCliente,
  updateEtichettaPod,
  type ClienteUpdate,
} from './api'

// =====================================================================
// QUERY KEYS — centralizzati per riuso e invalidazione type-safe
// =====================================================================

export const qk = {
  cliente: (codice: string | undefined) => ['cliente', codice] as const,
  bollette: (codiceCliente: string | undefined, codiceContratto: string | undefined) =>
    ['bollette', codiceCliente, codiceContratto] as const,
  dettaglioBolletta: (
    codiceCliente: string | undefined,
    codiceContratto: string | undefined,
    codice: string | undefined,
  ) => ['bollette', codiceCliente, codiceContratto, 'dettaglio', codice] as const,
  consumoAnno: (codiceCliente: string | undefined, codiceContratto: string | undefined) =>
    ['consumo', 'anno', codiceCliente, codiceContratto] as const,
  consumoBimestre: (codiceCliente: string | undefined, codiceContratto: string | undefined) =>
    ['consumo', 'bimestre', codiceCliente, codiceContratto] as const,
  news: () => ['news'] as const,
  promozioni: () => ['promozioni'] as const,
  messaggi: (codice: string | undefined) => ['messaggi', codice] as const,
  letture: (codiceContratto: string | undefined) => ['letture', codiceContratto] as const,
  currPower: (devices: string[] | undefined, codiceContratto: string | undefined) =>
    ['currPower', codiceContratto, devices?.join(',')] as const,
  currHour: (codiceContratto: string | undefined, fromDate: string | undefined) =>
    ['currHour', codiceContratto, fromDate] as const,
  currDay: (codiceContratto: string | undefined) =>
    ['currDay', codiceContratto] as const,
  currMonth: (codiceContratto: string | undefined) =>
    ['currMonth', codiceContratto] as const,
  prevMonth: (codiceContratto: string | undefined, fromDate: string | undefined) =>
    ['prevMonth', codiceContratto, fromDate] as const,
} as const

// =====================================================================
// QUERIES
// =====================================================================

/**
 * Carica i contratti + anagrafica per un codice cliente Cognito.
 * StaleTime alto (5 min): l'anagrafica cambia raramente.
 */
export function useCliente(codiceCliente: string | undefined) {
  return useQuery({
    queryKey: qk.cliente(codiceCliente),
    queryFn: () => fetchCliente(codiceCliente!),
    enabled: Boolean(codiceCliente),
    staleTime: 5 * 60_000,
  })
}

/**
 * Dettaglio voci per periodo di una specifica bolletta.
 * Ritorna `data.unavailable === true` se il backend dice "no dettaglio".
 */
export function useDettaglioBolletta(
  codiceCliente: string | undefined,
  codiceContratto: string | undefined,
  codice: string | undefined,
) {
  return useQuery({
    queryKey: qk.dettaglioBolletta(codiceCliente, codiceContratto, codice),
    queryFn: () =>
      fetchDettaglioBolletta(codiceCliente!, codiceContratto!, codice!),
    enabled: Boolean(codiceCliente && codiceContratto && codice),
    staleTime: 10 * 60_000,
  })
}

export function useBollette(
  codiceCliente: string | undefined,
  codiceContratto: string | undefined,
) {
  return useQuery({
    queryKey: qk.bollette(codiceCliente, codiceContratto),
    queryFn: () => fetchBolletteContratto(codiceCliente!, codiceContratto!),
    enabled: Boolean(codiceCliente && codiceContratto),
    staleTime: 60_000,
  })
}

export function useConsumoAnno(
  codiceCliente: string | undefined,
  codiceContratto: string | undefined,
) {
  return useQuery({
    queryKey: qk.consumoAnno(codiceCliente, codiceContratto),
    queryFn: () => fetchConsumoAnno(codiceCliente!, codiceContratto!),
    enabled: Boolean(codiceCliente && codiceContratto),
    staleTime: 5 * 60_000,
  })
}

export function useConsumoBimestre(
  codiceCliente: string | undefined,
  codiceContratto: string | undefined,
) {
  return useQuery({
    queryKey: qk.consumoBimestre(codiceCliente, codiceContratto),
    queryFn: () => fetchConsumoBimestre(codiceCliente!, codiceContratto!),
    enabled: Boolean(codiceCliente && codiceContratto),
    staleTime: 5 * 60_000,
  })
}

export function useNews() {
  return useQuery({
    queryKey: qk.news(),
    queryFn: fetchNews,
    staleTime: 10 * 60_000,
  })
}

export function usePromozioni() {
  return useQuery({
    queryKey: qk.promozioni(),
    queryFn: fetchPromozioni,
    staleTime: 30 * 60_000,
  })
}

export function useMessaggi(codiceCliente: string | undefined) {
  return useQuery({
    queryKey: qk.messaggi(codiceCliente),
    queryFn: () => fetchMessaggi(codiceCliente!),
    enabled: Boolean(codiceCliente),
    staleTime: 60_000,
  })
}

// ============================================================
// REALTIME / TIMED CONSUMPTIONS (richiedono device IoT)
// ============================================================

/**
 * Potenza istantanea iniziale. Da combinare con `useCurrPowerSubscription`
 * per ricevere update real-time via WebSocket AppSync.
 */
export function useCurrPower(
  devices: string[] | undefined,
  codiceContratto: string | undefined,
) {
  return useQuery({
    queryKey: qk.currPower(devices, codiceContratto),
    queryFn: () => fetchCurrPower(devices!, codiceContratto!),
    enabled: Boolean(devices && devices.length > 0 && codiceContratto),
    staleTime: 30_000,
  })
}

/**
 * Consumo dell'ora. Il backend Lambda non gestisce `from_date: null` (crash
 * Python `NoneType + relativedelta`) — passiamo sempre l'inizio dell'ora
 * corrente in formato ISO, come fa il mobile.
 */
function startOfCurrentHourISO(): string {
  const d = new Date()
  d.setMinutes(0, 0, 0)
  return d.toISOString()
}

/**
 * `placeholderData: keepPreviousData` evita il blink dello skeleton quando
 * la query key cambia (es. selettore ora) — mostra i dati precedenti
 * finché arrivano i nuovi.
 */

export function useCurrHour(codiceContratto: string | undefined, fromDate?: string) {
  const from = fromDate ?? startOfCurrentHourISO()
  return useQuery({
    queryKey: qk.currHour(codiceContratto, from),
    queryFn: () => fetchCurrHour(codiceContratto!, from),
    enabled: Boolean(codiceContratto),
    staleTime: 60_000,
    refetchInterval: 60_000, // ogni minuto
    placeholderData: keepPreviousData,
  })
}

export function useCurrDay(codiceContratto: string | undefined) {
  return useQuery({
    queryKey: qk.currDay(codiceContratto),
    queryFn: () => fetchCurrDay(codiceContratto!),
    enabled: Boolean(codiceContratto),
    staleTime: 15 * 60_000,
    placeholderData: keepPreviousData,
  })
}

export function useCurrMonth(codiceContratto: string | undefined) {
  return useQuery({
    queryKey: qk.currMonth(codiceContratto),
    queryFn: () => fetchCurrMonth(codiceContratto!),
    enabled: Boolean(codiceContratto),
    staleTime: 15 * 60_000,
    placeholderData: keepPreviousData,
  })
}

export function usePrevMonth(
  codiceContratto: string | undefined,
  fromDate: string | undefined,
) {
  return useQuery({
    queryKey: qk.prevMonth(codiceContratto, fromDate),
    queryFn: () => fetchPrevMonth(codiceContratto!, fromDate!),
    enabled: Boolean(codiceContratto && fromDate),
    staleTime: 30 * 60_000,
  })
}

export function useLetture(codiceContratto: string | undefined) {
  return useQuery({
    queryKey: qk.letture(codiceContratto),
    queryFn: () => fetchLetture(codiceContratto!),
    enabled: Boolean(codiceContratto),
    staleTime: 60_000,
  })
}

/**
 * Helper per pre-popolare in background le cache dei tab "Consumi" adiacenti.
 *
 * Le Lambda backend `currHour/Day/Month` sono lente (1-3s). Al primo render
 * della pagina avviamo in parallelo tutte e 3 le fetch così quando l'utente
 * cambia tab i dati sono già caldi. `prefetchQuery` non re-fetcha se la
 * key è già in cache e fresca.
 *
 * Da chiamare dentro un `useEffect` su mount della ConsumptionsPage.
 */
export function usePrefetchTimedTabs() {
  const qc = useQueryClient()
  return (codiceContratto: string) => {
    const hourFrom = startOfCurrentHourISO()
    qc.prefetchQuery({
      queryKey: qk.currHour(codiceContratto, hourFrom),
      queryFn: () => fetchCurrHour(codiceContratto, hourFrom),
      staleTime: 60_000,
    })
    qc.prefetchQuery({
      queryKey: qk.currDay(codiceContratto),
      queryFn: () => fetchCurrDay(codiceContratto),
      staleTime: 15 * 60_000,
    })
    qc.prefetchQuery({
      queryKey: qk.currMonth(codiceContratto),
      queryFn: () => fetchCurrMonth(codiceContratto),
      staleTime: 15 * 60_000,
    })
  }
}

// =====================================================================
// MUTATIONS
// =====================================================================

export function useMarkBollettaVista() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { codice_cliente: string; codice_contratto: string; invoice: string }) =>
      markBollettaVista(input.codice_cliente, input.codice_contratto, input.invoice),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.bollette(vars.codice_cliente, vars.codice_contratto) })
    },
  })
}

export function useSendAutolettura() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: sendAutoLettura,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.letture(vars.codice_contratto) })
      qc.invalidateQueries({ queryKey: qk.cliente(vars.codice_cliente) })
    },
  })
}

export function useUpdateCliente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { codice_cliente: string; vals: ClienteUpdate }) =>
      updateCliente(input.codice_cliente, input.vals),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.cliente(vars.codice_cliente) })
    },
  })
}

export function useUpdateEtichettaPod() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { codice_cliente: string; codice_pod: string; etichetta: string | null }) =>
      updateEtichettaPod(input.codice_pod, input.etichetta),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.cliente(vars.codice_cliente) })
    },
  })
}

export function useMarkMessaggioRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { codice_cliente: string; datetime: string }) =>
      markMessaggioRead(input.codice_cliente, input.datetime),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.messaggi(vars.codice_cliente) })
    },
  })
}
